import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, HandMetal, Users } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

function visitorId() {
  let v = localStorage.getItem("fc_visitor");
  if (!v) {
    v = `v_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem("fc_visitor", v);
  }
  return v;
}

/**
 * "I'll be there" — a lightweight headcount for a Friday date. No account or RSVP
 * required: visitors are remembered by a browser id, members by their account.
 */
export default function AttendButton({ date, size = "lg", className = "" }) {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [asking, setAsking] = useState(false);
  const [name, setName] = useState(() => localStorage.getItem("fc_visitor_name") || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!date) return;
    api.get(`/attending/${date}`, { params: { visitor_id: visitorId() } }).then((r) => setInfo(r.data)).catch(() => {});
  }, [date, user?.user_id]);

  const commit = async () => {
    setBusy(true);
    try {
      const body = { date, visitor_id: visitorId(), name: user ? undefined : name.trim() || undefined };
      const r = await api.post("/attending", body);
      if (!user && name.trim()) localStorage.setItem("fc_visitor_name", name.trim());
      setInfo(r.data);
      setAsking(false);
      toast.success("See you Friday!");
    } catch (e) {
      toast.error("Couldn't save that — try again in a moment");
    } finally { setBusy(false); }
  };
  const undo = async () => {
    setBusy(true);
    try { const r = await api.delete(`/attending/${date}`, { params: { visitor_id: visitorId() } }); setInfo(r.data); } catch (e) { /* noop */ } finally { setBusy(false); }
  };

  const onClick = () => {
    if (info?.me) return undo();
    if (!user && !name) return setAsking(true);
    return commit();
  };

  const count = info?.count || 0;
  const others = info?.me ? count - 1 : count;
  const names = (info?.names || []).slice(0, 3);

  return (
    <div className={`flex flex-col gap-2 ${className}`} data-testid="attend-block">
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" size={size} variant={info?.me ? "secondary" : "outline"} disabled={busy || !date} onClick={onClick} className={info?.me ? "border border-primary/40" : ""} data-testid="ill-be-there-button" aria-pressed={!!info?.me}>
          {info?.me ? <Check className="mr-1.5 h-4 w-4 text-primary" /> : <HandMetal className="mr-1.5 h-4 w-4" />}
          {info?.me ? "You're on the list" : "I'll be there"}
        </Button>
        {count > 0 && (
          <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="headcount-badge">
            <Users className="h-4 w-4 text-primary" />
            <span className="font-semibold text-foreground" data-testid="headcount-count">{count}</span>
            {count === 1 ? " person" : " people"} coming{names.length > 0 && others > 0 ? ` · ${names.filter((n) => !(info?.me && n === (user?.name || name).split(" ")[0])).slice(0, 2).join(", ")}${others > 2 ? " & more" : ""}` : ""}
          </span>
        )}
      </div>
      {asking && (
        <form onSubmit={(e) => { e.preventDefault(); commit(); }} className="flex w-full max-w-sm items-center gap-2 rounded-xl border border-border bg-card p-2 shadow-sm" data-testid="attend-name-form">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="First name (optional)" autoFocus className="h-9 flex-1 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary" data-testid="attend-name-input" />
          <Button type="submit" size="sm" disabled={busy} data-testid="attend-confirm">Count me in</Button>
        </form>
      )}
      <p className="text-xs text-muted-foreground">No RSVP needed — this just helps us set the room.</p>
    </div>
  );
}
